import { I18n } from '@calendly/i18n'
import moment from 'moment-timezone'

import { formatDate } from 'services/time_format'

/**
 * @returns {string}
 */
export const formatDateLarge = (date, timezone, dateNotation = 'american') => {
  // i18n-tasks-use t('time.formats.american.date_large')
  // i18n-tasks-use t('time.formats.european_standard.date_large')
  const format = I18n.t(`time.formats.${dateNotation}.date_large`)
  return formatDate(moment.tz(date, timezone), format)
}

/**
 * @returns {string}
 */
export const formatDateSmallWithYear = (date, timezone, dateNotation = 'american') => {
  // i18n-tasks-use t('time.formats.american.date_small_with_year')
  // i18n-tasks-use t('time.formats.european_standard.date_small_with_year')
  const format = I18n.t(`time.formats.${dateNotation}.date_small_with_year`)
  return formatDate(moment.tz(date, timezone), format)
}

// TODO: https://calendlyapp.atlassian.net/browse/CAL-64473
// Note: USD has no suffix for SAML SSO addon. That is the only addon
// this is used for right now but we will need to update this if we
// have more addons besides MI that use a USD suffix.
export const changeAddonCurrency = (newCurrency, addons = [], audVariant = 'variant_1') => {
  const supportedCurrencies = ['aud', 'brl', 'eur', 'gbp', 'inr']
  const currencySuffixRegex = new RegExp(`-(${supportedCurrencies.join('|')})(?:-var[12])?$`)
  const lowerCaseNewCurrency = newCurrency.toLowerCase()

  if (!supportedCurrencies.includes(lowerCaseNewCurrency) && lowerCaseNewCurrency !== 'usd') {
    return addons.map(() => null)
  }

  let suffix = lowerCaseNewCurrency === 'usd' ? '' : `-${lowerCaseNewCurrency}`

  if (lowerCaseNewCurrency === 'aud') {
    suffix += audVariant === 'variant_2' ? '-var2' : '-var1'
  }

  return addons.map((addon) => addon.replace(currencySuffixRegex, '') + suffix)
}

// ideally would pass base_sku_id and periodUnit
export const standardizeSku = (sku, { trimPeriodUnit = false } = {}) => {
  // Ensure sku is a string - this is a critical safety check
  if (typeof sku !== 'string') {
    console.error('standardizeSku: sku is not a string', { sku, type: typeof sku })
    // Try to extract id if it's an object
    if (typeof sku === 'object' && sku != null && 'id' in sku && typeof sku.id === 'string') {
      console.warn('standardizeSku: extracted id from object', { original: sku, id: sku.id })
      return standardizeSku(sku.id, { trimPeriodUnit })
    }
    throw new Error(`standardizeSku: expected string, got ${typeof sku}`)
  }

  const normalizedSku = sku.replaceAll('-', '_').toLowerCase()
  const standardizedSku = normalizedSku
    .replace(/_usd|_aud|_brl|_eur|_gbp|_inr/g, '') // remove currency
    .replace(/_tiered/g, '') // remove pricing model
    .replace(/_var1|_var2/g, '') // remove variations
    .replace(/apple_|ios_/g, '') // remove purchase source
    .replace(/__variant_\d+/g, '') // remove experiment / price-test suffixes
    .replace(/__legacy(?:_\d+)?/g, '') // remove legacy cohort suffixes

  if (!trimPeriodUnit) return standardizedSku

  return standardizedSku
    .replace('monthly', 'month')
    .replace('yearly', 'year')
}

// i18n-tasks-use t('publisher.billing.tiers.basic')
// i18n-tasks-use t('publisher.billing.tiers.essentials')
// i18n-tasks-use t('publisher.billing.tiers.premium')
// i18n-tasks-use t('publisher.billing.tiers.pro')
// i18n-tasks-use t('publisher.billing.tiers.professional')
// i18n-tasks-use t('publisher.billing.tiers.standard')
// i18n-tasks-use t('publisher.billing.tiers.standard_scheduling')
// i18n-tasks-use t('publisher.billing.tiers.standard_notetaker')
// i18n-tasks-use t('publisher.billing.tiers.teams')
// i18n-tasks-use t('publisher.billing.tiers.teams_scheduling')
// i18n-tasks-use t('publisher.billing.tiers.teams_notetaker')
// i18n-tasks-use t('publisher.billing.tiers.teams_plus')
// i18n-tasks-use t('publisher.billing.tiers.enterprise')
export const humanizeTier = (tier) => (I18n.t(`publisher.billing.tiers.${tier}`))

// i18n-tasks-use t('publisher.billing.plans.essentials_month')
// i18n-tasks-use t('publisher.billing.plans.essentials_year')
// i18n-tasks-use t('publisher.billing.plans.premium_month')
// i18n-tasks-use t('publisher.billing.plans.premium_year')
// i18n-tasks-use t('publisher.billing.plans.pro_month')
// i18n-tasks-use t('publisher.billing.plans.pro_year')
// i18n-tasks-use t('publisher.billing.plans.professional_month')
// i18n-tasks-use t('publisher.billing.plans.professional_year')
// i18n-tasks-use t('publisher.billing.plans.standard_month')
// i18n-tasks-use t('publisher.billing.plans.standard_notetaker_month')
// i18n-tasks-use t('publisher.billing.plans.standard_notetaker_year')
// i18n-tasks-use t('publisher.billing.plans.standard_scheduling_month')
// i18n-tasks-use t('publisher.billing.plans.standard_scheduling_year')
// i18n-tasks-use t('publisher.billing.plans.standard_year')
// i18n-tasks-use t('publisher.billing.plans.teams_month')
// i18n-tasks-use t('publisher.billing.plans.teams_notetaker_month')
// i18n-tasks-use t('publisher.billing.plans.teams_notetaker_year')
// i18n-tasks-use t('publisher.billing.plans.teams_year')
// i18n-tasks-use t('publisher.billing.plans.teams_plus_month')
// i18n-tasks-use t('publisher.billing.plans.teams_plus_year')
// i18n-tasks-use t('publisher.billing.plans.teams_scheduling_month')
// i18n-tasks-use t('publisher.billing.plans.teams_scheduling_year')
// i18n-tasks-use t('publisher.billing.plans.enterprise_month')
// i18n-tasks-use t('publisher.billing.plans.enterprise_year')
export const humanizePlan = (plan) => {
  if (plan == null) {
    console.error('humanizePlan: plan is null or undefined', plan)
    return ''
  }

  let planId = null

  // Extract plan ID from string or object
  if (typeof plan === 'string') {
    planId = plan.trim()
  } else if (typeof plan === 'object' && !Array.isArray(plan) && plan.id != null) {
    planId = typeof plan.id === 'string' ? plan.id : null
  }

  // Validate planId
  if (!planId || typeof planId !== 'string') {
    console.error('humanizePlan: invalid plan or plan.id', { plan, planId })
    return ''
  }

  const cleanPlanId = planId.trim()

  // Check for invalid string formats (object serialization, Ruby hash syntax)
  if (!cleanPlanId
      || cleanPlanId === '[object Object]'
      || cleanPlanId.includes('=>')
      || cleanPlanId.startsWith('{')
      || cleanPlanId.startsWith('[')
      || /^\{:/.test(cleanPlanId)) {
    console.error('humanizePlan: planId looks like serialized object', { plan, cleanPlanId })
    return ''
  }

  try {
    const sku = standardizeSku(cleanPlanId, { trimPeriodUnit: true })
    if (!sku || typeof sku !== 'string') {
      console.error('humanizePlan: standardizeSku returned invalid result', { plan, cleanPlanId, sku })
      return ''
    }

    return I18n.t(`publisher.billing.plans.${sku}`)
  } catch (error) {
    console.error('humanizePlan: error processing plan', { plan, cleanPlanId, error })
    return ''
  }
}

// i18n-tasks-use t('publisher.billing.addons.saml_sso_yearly')
// i18n-tasks-use t('publisher.billing.addons.saml_sso_monthly')
// i18n-tasks-use t('publisher.billing.addons.meeting_intelligence_beta_yearly')
// i18n-tasks-use t('publisher.billing.addons.meeting_intelligence_beta_monthly')
export const humanizeAddonSku = (addonSku) => (I18n.t(`publisher.billing.addons.${standardizeSku(addonSku)}`))

export const sentenceCasePlan = (plan) => {
  const humanizedPlan = humanizePlan(plan)
  return humanizedPlan.charAt(0).toUpperCase() + humanizedPlan.slice(1)
}
const moveEuroSign = (amount) => `€${amount.replace(/\u00A0€/, '')}`

export const formatAmount = (amount, currency, { forceDecimal = false } = {}) => {
  const currencyLocales = {
    usd: 'en-US',
    aud: 'en-AU',
    brl: 'pt-BR',
    eur: 'de-DE',
    gbp: 'en-GB',
    inr: 'en-IN'
  }
  const locale = currencyLocales[currency.toLowerCase()] || 'en-US'

  const wholeDisplay = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
  const decimalDisplay = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  const formattedAmount = amount % 1 === 0 && !forceDecimal
    ? wholeDisplay.format(amount)
    : decimalDisplay.format(amount)

  return locale === 'de-DE' ? moveEuroSign(formattedAmount) : formattedAmount
}

export const displayAmountFromCents = (cost, currency, showCurrencyCode = false, { forceDecimal = false } = {}) => {
  const amount = formatAmount(cost / 100, currency, { forceDecimal })

  if (showCurrencyCode) {
    return amount.concat(` ${currency}`)
  }
  return amount
}

export const displayMonthlyAmountFromCentsForBillingPeriod = (cost, billingPeriod, currency, showCurrencyCode = false) => {
  const monthlyCost = (billingPeriod === 'year' || billingPeriod === 'yearly') ? cost / 12 : cost

  return displayAmountFromCents(monthlyCost, currency, showCurrencyCode)
}

export const savingsWithDiscountApplied = (amount, discountPercent) => {
  if (!discountPercent) return amount

  return Math.round(amount * (1 - (discountPercent / 100)))
}

export const transactionTitle = (transaction) => {
  if (transaction.type === 'refund') {
    return I18n.t('publisher.billing.history.refund')
  }

  if (transaction.invoiceRecord) {
    switch (transaction.invoiceRecord.invoice_type) {
      case 'plan':
      case 'addon':
      case 'plan + addon':
        return transaction.invoiceRecord.title
      default:
        return I18n.t('publisher.billing.history.charge')
    }
  }

  return I18n.t('publisher.billing.history.charge')
}

export const transactionStatus = (transaction) => {
  if (transaction.type === 'refund') return I18n.t('publisher.billing.history.refunded')

  return (transaction.paid
    ? I18n.t('publisher.billing.history.paid')
    : I18n.t('publisher.billing.history.failed')
  )
}

export const transactionTotal = (transaction, currency) => {
  let amount
  if (transaction.type === 'refund') {
    amount = (transaction.total / 100) * -1
  } else {
    amount = (transaction.invoiceRecord ? transaction.invoiceRecord.amount_paid : transaction.total) / 100
  }

  return formatAmount(amount, currency)
}

export const calculateSavings = ({
  isYearlyTiered,
  pricingTiers,
  quantity,
  cost,
  perUnitSavings,
  monthlyCost,
}) => {
  if (isYearlyTiered && pricingTiers && monthlyCost) {
    const totalCost = pricingTiers.reduce((total, tier) => {
      const { amount_cents: amountCents, starting, ending } = tier

      if (quantity < starting) return total

      const endingCount = ending ? Math.min(quantity, ending) : quantity
      const quantityInTier = endingCount - starting + 1

      return total + (amountCents * quantityInTier)
    }, 0)

    if (quantity > 0) {
      const actualCost = totalCost / quantity / 12
      const savings = (monthlyCost * 12 * quantity) - totalCost

      return { savings, actualCost }
    }
  }

  const savings = quantity * perUnitSavings

  return { savings, actualCost: cost }
}

export const calculateDaysSinceTrialStart = (trialStartDate) => (
  moment().diff(moment(trialStartDate), 'days')
)
